import {
  ChangeEvent,
  ClipboardEvent,
  FC,
  FocusEventHandler,
  FormEvent,
} from 'react';
import { format } from '@react-input/mask';
import type { Track } from '@react-input/mask';

import { default as Input, MaskInputProps } from './InputMask';

const US_PHONE_MASK = '+1 (___) ___-____';
const PHONE_REPLACEMENT = { _: /\d/ };

/** Explicit non-US country code, e.g. +2, +44, +256 (not +1). */
const NON_US_COUNTRY_CODE = /^\s*\+(?!1(?:\D|$))/;

const isInternationalPhoneValue = (value: string) =>
  NON_US_COUNTRY_CODE.test(value.trim());

const getPhoneDigits = (value: string) => value.replace(/\D/g, '');

const formatUSPhoneValue = (value: string) =>
  format(value.replace(/\+1/g, ''), {
    mask: US_PHONE_MASK,
    replacement: PHONE_REPLACEMENT,
  });

/** Normalize a US paste into the US mask. Returns null for non-US pastes. */
const formatUSPaste = (text: string): string | null => {
  const trimmed = text.trim();
  if (!trimmed) return '';
  if (isInternationalPhoneValue(trimmed)) return null;

  let digits = getPhoneDigits(trimmed);
  if (digits.length >= 11 && digits.startsWith('1')) {
    digits = digits.slice(1);
  }

  return format(digits.slice(0, 10), {
    mask: US_PHONE_MASK,
    replacement: PHONE_REPLACEMENT,
  });
};

const emitChange = (props: MaskInputProps, value: string) => {
  props.onChange?.({
    target: { name: props.name ?? '', value },
  } as unknown as ChangeEvent<HTMLInputElement>);
};

/**
 * Typing (1 char) → unchanged (US mask).
 * Bulk US insert → national digits for the US mask.
 * Non-US bulk insert is handled by paste / beforeinput (blocked here).
 */
const trackPhoneInput: Track = ({ inputType, data }) => {
  if (inputType !== 'insert' || data == null || data.length <= 1) {
    return;
  }

  if (isInternationalPhoneValue(data)) {
    return false;
  }

  let digits = getPhoneDigits(data);
  if (digits.length >= 11 && digits.startsWith('1')) {
    digits = digits.slice(1);
  }

  return digits.slice(0, 10);
};

const applyPastedText = (props: MaskInputProps, text: string) => {
  const trimmed = text.trim();
  if (!trimmed) return false;

  const usValue = formatUSPaste(trimmed);
  if (usValue === null) {
    // Keep exact pasted international text; validation shows US-only error.
    emitChange(props, trimmed);
    return true;
  }

  emitChange(props, usValue);
  return true;
};

const PhoneNumberInput: FC<MaskInputProps> = props => {
  const { onBlur, ...rest } = props;
  const rawValue = String(props.value ?? '');
  const isIntlValue = isInternationalPhoneValue(rawValue);

  // Typing / US paste → US mask. Non-US paste → plain text (no dynamic intl mask).
  const mask = isIntlValue ? '' : US_PHONE_MASK;
  const phoneValue = isIntlValue ? rawValue : formatUSPhoneValue(rawValue);

  const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
    const text = event.clipboardData.getData('text');
    if (!text?.trim()) return;

    event.preventDefault();
    applyPastedText(props, text);
  };

  // Stronger iOS Safari support than onPaste alone.
  const handleBeforeInput = (event: FormEvent<HTMLInputElement>) => {
    const nativeEvent = event.nativeEvent as InputEvent;
    if (nativeEvent.inputType !== 'insertFromPaste') return;

    const text = nativeEvent.data ?? '';
    if (!text.trim()) return;

    event.preventDefault();
    applyPastedText(props, text);
  };

  const handleBlur: FocusEventHandler<HTMLInputElement> = event => {
    onBlur?.(event);
  };

  return (
    <Input
      {...rest}
      inputMode="tel"
      mask={mask}
      onBeforeInput={handleBeforeInput}
      onBlur={handleBlur}
      onPaste={handlePaste}
      replacement={isIntlValue ? undefined : PHONE_REPLACEMENT}
      track={isIntlValue ? undefined : trackPhoneInput}
      value={phoneValue}
    />
  );
};

const formatPhoneValue = (value?: string | number | readonly string[]) => {
  if (!value) return '';

  const raw = String(value);
  if (isInternationalPhoneValue(raw)) {
    return raw;
  }

  return formatUSPhoneValue(raw);
};

export { PhoneNumberInput, formatPhoneValue };
